import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { STORAGE_URL } from '../services/api';
import { 
    Briefcase, Plus, Trash2, MapPin, DollarSign, Users, Mail, Phone, 
    UserCheck, LogOut, Edit, X, CheckCircle, Save, CheckSquare, 
    User, FileText, CheckCircle2, ChevronRight, ChevronLeft, 
    ChevronDown, ChevronUp, FileText as FileIcon, XCircle
} from 'lucide-react';
import TopBar from '../components/TopBar';
import toast from 'react-hot-toast';

const STEPS = [
  { id: 1, title: "Basic Info", icon: Briefcase },
  { id: 2, title: "Job Details", icon: CheckSquare },
  { id: 3, title: "Candidate Info", icon: User },
  { id: 4, title: "Description", icon: FileText },
  { id: 5, title: "Confirm", icon: CheckCircle2 }
];

export default function EmployerDashboard() {
    const [activeTab, setActiveTab] = useState('jobs'); // 'jobs', 'post-job', 'applied', 'shortlisted', 'rejected'
    const [expandedMenu, setExpandedMenu] = useState('jobs');
    const [employer, setEmployer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    const [currentStep, setCurrentStep] = useState(1);
    const [editingJobId, setEditingJobId] = useState(null); 

    const [dbCategories, setDbCategories] = useState([]);
    const [dbCities, setDbCities] = useState([]);
    const [dbLocalities, setDbLocalities] = useState([]);
    const [loadingLocalities, setLoadingLocalities] = useState(false);
    
    const [dbInterviewLocalities, setDbInterviewLocalities] = useState([]);
    const [loadingInterviewLocalities, setLoadingInterviewLocalities] = useState(false);

    const [skillInput, setSkillInput] = useState("");
    
    const initialJobForm = {
        title: "", category: "", vacancies: "1", job_type: "Full-Time", contractual: false,
        work_location_type: "Work from office", city: "", locality: "", deadline: "", 
        salary_min: "", salary_max: "", benefits: [], shift: "Day", working_days: "6 days working",
        interview_type: "In-person", interview_city: "", interview_locality: "", deposit_required: "No",
        gender: "Any", qualification: "12th Pass", exp_min: "0", exp_max: "1",
        freshers_only: false, industry: "ANY INDUSTRY", skills: [], english_fluency: "Understands Good English",
        description: "", status: "active"
    };

    const [jobFormData, setJobFormData] = useState(initialJobForm);
    const minDate = new Date().toISOString().split("T")[0];

    const [selectedJobApplicants, setSelectedJobApplicants] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileData, setProfileData] = useState({ company_name: '', name: '', email: '', phone: '', website: '' });
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    useEffect(() => {
        const storedEmployer = localStorage.getItem('employer');
        if (storedEmployer) {
            const parsedEmp = JSON.parse(storedEmployer);
            setEmployer(parsedEmp);
            setProfileData(parsedEmp);
            fetchInitialData(parsedEmp.id);
        } else {
            window.location.href = '/employer/login'; 
        }
    }, []);

    const fetchInitialData = async (employerId) => {
        try {
            const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [dashRes, catRes, cityRes] = await Promise.all([
                api.get(`/employer/profile/${employerId}`, { headers }),
                api.get("/categories", { headers }),
                api.get("/cities", { headers }).catch(() => ({ data: [] }))
            ]);

            if (dashRes.data.status === 'success') {
                setEmployer(dashRes.data.employer);
                setJobs(dashRes.data.jobs || []);
            }
            setDbCategories(catRes.data);
            setDbCities(cityRes.data || []);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setIsInitializing(false);
        }
    };

    const confirmLogout = () => {
        localStorage.removeItem('employer');
        localStorage.removeItem('employer_token');
        localStorage.removeItem('token');
        window.location.href = '/'; 
    };

    // 🌟 UPDATED UPDATE PROFILE FUNCTION
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            const response = await api.put(`/employer/profile/${employer.id}`, profileData);
            
            if (response.data.success || response.data.status === 'success') {
                const updatedEmployer = response.data.data || response.data.employer;
                
                setEmployer(updatedEmployer);
                localStorage.setItem('employer', JSON.stringify(updatedEmployer));
                setIsEditingProfile(false);
                toast.success('Profile updated successfully! 🎉');
            } else {
                toast.error(response.data.message || 'Failed to update profile.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update profile.');
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setJobFormData({ ...jobFormData, [name]: type === "checkbox" ? checked : value });
    };

    const handlePillSelect = (field, value) => {
        setJobFormData({ ...jobFormData, [field]: value });
    };

    const handleMultiSelect = (field, value) => {
        const currentValues = jobFormData[field];
        if (currentValues.includes(value)) {
            setJobFormData({ ...jobFormData, [field]: currentValues.filter(v => v !== value) });
        } else {
            setJobFormData({ ...jobFormData, [field]: [...currentValues, value] });
        }
    };

    const handleSkillKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const val = skillInput.trim();
            if (val && !jobFormData.skills.includes(val)) {
                setJobFormData({ ...jobFormData, skills: [...jobFormData.skills, val] });
            }
            setSkillInput("");
        }
    };
    const removeSkill = (skillToRemove) => {
        setJobFormData({ ...jobFormData, skills: jobFormData.skills.filter(skill => skill !== skillToRemove) });
    };

    const handleCityChange = async (e) => {
        const selectedCity = e.target.value;
        setJobFormData({ ...jobFormData, city: selectedCity, locality: "" }); 
        if (selectedCity) {
            setLoadingLocalities(true);
            try {
                const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const res = await api.get(`/localities?city=${selectedCity}`, { headers });
                setDbLocalities(res.data || []);
            } catch (err) {
                console.error("Error fetching localities:", err);
            } finally { 
                setLoadingLocalities(false); 
            }
        } else { 
            setDbLocalities([]); 
        }
    };

    const handleInterviewCityChange = async (e) => {
        const selectedCity = e.target.value;
        setJobFormData({ ...jobFormData, interview_city: selectedCity, interview_locality: "" }); 
        if (selectedCity) {
            setLoadingInterviewLocalities(true);
            try {
                const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const res = await api.get(`/localities?city=${selectedCity}`, { headers });
                setDbInterviewLocalities(res.data || []);
            } catch (err) {
                console.error("Error fetching interview localities:", err);
            } finally { 
                setLoadingInterviewLocalities(false); 
            }
        } else { 
            setDbInterviewLocalities([]); 
        }
    };

    const handleEditJob = async (job) => {
        setEditingJobId(job.id);
        
        let sMin = "", sMax = "";
        if (job.salary_range) {
           const nums = job.salary_range.replace(/[^\d-]/g, '').split('-');
           sMin = nums[0] || ""; sMax = nums[1] || "";
        }

        let locCity = "", locLocality = "";
        if (job.location) {
           const locParts = job.location.split(', ');
           if (locParts.length > 1) {
             locLocality = locParts[0]; locCity = locParts.slice(1).join(', ');
           } else { locCity = job.location; }
        }

        if (locCity) {
            setLoadingLocalities(true);
            const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            api.get(`/localities?city=${locCity}`, { headers }).then(res => setDbLocalities(res.data)).finally(() => setLoadingLocalities(false));
        }

        const parsedSkills = job.skills ? (typeof job.skills === 'string' ? JSON.parse(job.skills) : job.skills) : [];
        const parsedBenefits = job.benefits ? (typeof job.benefits === 'string' ? JSON.parse(job.benefits) : job.benefits) : [];

        setJobFormData({
            ...initialJobForm,
            title: job.title || '', 
            category: job.category || '',
            vacancies: job.vacancies?.toString() || "1",
            job_type: job.job_type || 'Full-Time',
            contractual: job.contractual || false,
            work_location_type: job.work_location_type || 'Work from office',
            city: locCity,
            locality: locLocality,
            deadline: job.deadline ? job.deadline.split('T')[0] : '',
            salary_min: sMin,
            salary_max: sMax,
            benefits: parsedBenefits,
            shift: job.shift || 'Day',
            working_days: job.working_days || '6 days working',
            interview_type: job.interview_type || 'In-person',
            interview_city: job.interview_city || '',
            interview_locality: job.interview_locality || '',
            deposit_required: job.deposit_required || 'No',
            gender: job.gender || 'Any',
            qualification: job.qualification || '12th Pass',
            freshers_only: job.freshers_only || false,
            skills: parsedSkills,
            english_fluency: job.english_fluency || 'Understands Good English',
            description: job.description || '',
            status: job.status || 'active'
        });

        setCurrentStep(1);
        setActiveTab('post-job');
    };

    const validateStep = () => {
        if (currentStep === 1) {
            if (!jobFormData.title) return toast.error("Job Title is required");
            if (!jobFormData.category) return toast.error("Category is required");
            if (!jobFormData.city) return toast.error("City is required");
            if (!jobFormData.deadline) return toast.error("Application Deadline is required"); 
        }
        if (currentStep === 2) {
            if (!jobFormData.salary_min || !jobFormData.salary_max) return toast.error("Salary range is required");
        }
        if (currentStep === 3) {
            if (jobFormData.skills.length === 0) return toast.error("Please add at least one skill");
        }
        if (currentStep === 4) {
            if (!jobFormData.description) return toast.error("Job Description is required");
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, 5));
    };

    const handlePrev = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const handleSaveJob = async () => {
        if (!validateStep()) return;
        setLoading(true);

        try {
            const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
            const finalData = {
                ...jobFormData,
                employer_id: employer.id,
                vacancies: parseInt(jobFormData.vacancies),
                salary_range: `₹${jobFormData.salary_min}-₹${jobFormData.salary_max}`,
                location: jobFormData.locality ? `${jobFormData.locality}, ${jobFormData.city}` : jobFormData.city,
                experience: jobFormData.freshers_only ? "Fresher" : `${jobFormData.exp_min}-${jobFormData.exp_max} Years`,
            };

            let response;
            if (editingJobId) {
                response = await api.put(`/jobs/${editingJobId}`, finalData, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                toast.success('Job updated successfully!');
            } else {
                response = await api.post('/jobs', finalData, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                toast.success('Job posted successfully!');
            }

            if (response.status === 200 || response.status === 201 || response.data.status === 'success') {
                setJobFormData(initialJobForm);
                setEditingJobId(null);
                setCurrentStep(1);
                fetchInitialData(employer.id);
                setActiveTab('jobs');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save job.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this job posting?')) return;
        try {
            const token = localStorage.getItem('employer_token') || localStorage.getItem('token');
            await api.delete(`/jobs/${jobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success('Job deleted successfully!');
            fetchInitialData(employer.id);
        } catch (error) {
            toast.error('Failed to delete job.');
        }
    };

    const allCandidates = jobs.flatMap(job => 
        (job.candidates || []).map(candidate => ({ ...candidate, job_title: job.title }))
    );
    const totalApplicantsCount = allCandidates.length;
    const getStatus = (status) => status ? status.toLowerCase() : 'applied';
    const appliedCandidates = allCandidates.filter(c => getStatus(c.status) === 'applied' || getStatus(c.status) === 'pending');
    const shortlistedCandidates = allCandidates.filter(c => getStatus(c.status) === 'shortlisted');
    const rejectedCandidates = allCandidates.filter(c => getStatus(c.status) === 'rejected');

    const renderApplicantsTable = (candidatesList, title) => (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-extrabold text-gray-900 text-lg">{title} ({candidatesList.length})</h3>
            </div>
            {candidatesList.length === 0 ? (
                <div className="p-16 text-center text-gray-400 font-medium text-sm">No candidates found in this category.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 text-gray-400 text-xs uppercase tracking-wider font-extrabold">
                                <th className="py-4 px-6">Candidate Details</th>
                                <th className="py-4 px-6">Job Role</th>
                                <th className="py-4 px-6">Contact Info</th>
                                <th className="py-4 px-6 text-center">Resume</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {candidatesList.map(candidate => (
                                <tr key={candidate.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="py-4 px-6">
                                        <div className="font-extrabold text-gray-900">{candidate.name || candidate.full_name}</div>
                                        <span className="text-[10px] uppercase font-bold text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded border border-yellow-200 mt-1 inline-block">{candidate.status || 'Applied'}</span>
                                    </td>
                                    <td className="py-4 px-6 font-bold text-gray-700">{candidate.job_title}</td>
                                    <td className="py-4 px-6 space-y-1">
                                        <div className="text-gray-600 font-medium flex items-center gap-1.5"><Mail size={13} className="text-blue-500"/> {candidate.email}</div>
                                        <div className="text-gray-600 font-medium flex items-center gap-1.5"><Phone size={13} className="text-emerald-500"/> {candidate.phone}</div>
                                    </td>
                                    <td className="py-4 px-6 text-center">
                                        {candidate.resume_url || candidate.resume ? (
                                            <a href={STORAGE_URL(candidate.resume_url || candidate.resume)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 font-bold text-xs rounded-xl transition-colors">
                                                <FileIcon size={14}/> View Resume
                                            </a>
                                        ) : <span className="text-gray-400 text-xs font-semibold">No Resume</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const Pill = ({ active, label, onClick }) => (
        <button type="button" onClick={onClick} className={`px-4 py-2 text-[13px] font-bold rounded-xl border transition-all ${active ? "bg-[#FED547]/20 border-[#FED547] text-yellow-800 shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:border-yellow-300 hover:bg-yellow-50/30"}`}>
            {label}
        </button>
    );

    if (isInitializing || !employer) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
                <TopBar />
                <div className="flex-grow flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FED547]"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col w-full relative font-sans">
            <TopBar />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex-grow w-full space-y-8">
                
                {/* Profile Banner */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-[#FED547] text-black font-extrabold text-2xl rounded-2xl flex items-center justify-center shadow-sm overflow-hidden">
                            {employer.company_name ? employer.company_name.charAt(0).toUpperCase() : 'E'}
                        </div>
                        <div>
                            <h1 className="text-xl font-extrabold text-gray-900">Welcome back, {employer.name} 👋</h1>
                            <p className="text-sm font-semibold text-gray-500">{employer.company_name} - <span className="text-gray-400">{employer.email}</span></p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsEditingProfile(true)} className="bg-gray-100 text-gray-800 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-200 transition-colors flex items-center gap-2 cursor-pointer">
                            <Edit size={16} /> Edit Profile
                        </button>
                        <button onClick={() => setShowLogoutConfirm(true)} className="bg-red-50 text-red-600 p-3 rounded-xl hover:bg-red-100 transition-colors cursor-pointer" title="Logout">
                            <LogOut size={18} />
                        </button>
                    </div>
                </div>

                {/* Quick Stats Bar */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div className="bg-white p-5 rounded-3xl border border-gray-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
                        <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner"><Briefcase size={24} /></div>
                        <div><p className="text-xs text-gray-500 uppercase font-extrabold tracking-wider">Jobs Posted</p><h3 className="text-2xl font-black text-gray-900">{jobs.length}</h3></div>
                    </div>
                    <div className="bg-white p-5 rounded-3xl border border-gray-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
                        <div className="w-14 h-14 bg-[#FED547]/20 text-yellow-700 rounded-2xl flex items-center justify-center shadow-inner"><Users size={24} /></div>
                        <div><p className="text-xs text-gray-500 uppercase font-extrabold tracking-wider">Applicants</p><h3 className="text-2xl font-black text-gray-900">{totalApplicantsCount}</h3></div>
                    </div>
                    <div className="bg-white p-5 rounded-3xl border border-gray-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
                        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner"><CheckCircle size={24} /></div>
                        <div><p className="text-xs text-gray-500 uppercase font-extrabold tracking-wider">Status</p><h3 className="text-xl font-black text-emerald-600 capitalize">Active</h3></div>
                    </div>
                </div>

                {/* Dashboard Layout Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
                    
                    {/* Sidebar Navigation */}
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-5 sticky top-24 space-y-2">
                        <button onClick={() => { setActiveTab('jobs'); setEditingJobId(null); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-extrabold transition-all ${activeTab === 'jobs' ? 'bg-[#FED547] text-black shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}>
                            <Briefcase size={18} /> Manage Jobs
                            <span className="ml-auto bg-white/60 px-2 py-0.5 rounded-md text-xs">{jobs.length}</span>
                        </button>

                        <button onClick={() => { setActiveTab('post-job'); setEditingJobId(null); setJobFormData(initialJobForm); setCurrentStep(1); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-extrabold transition-all ${activeTab === 'post-job' && !editingJobId ? 'bg-[#FED547] text-black shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}>
                            <Plus size={18} /> Post New Job
                        </button>

                        <div className="pt-2">
                            <button 
                                onClick={() => setExpandedMenu(expandedMenu === 'applicants' ? '' : 'applicants')}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-extrabold text-yellow-900 bg-yellow-50/80 border border-yellow-200/60 transition-colors"
                            >
                                <div className="flex items-center gap-3"><Users size={18} className="text-yellow-700"/> Manage Applicants</div>
                                {expandedMenu === 'applicants' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>

                            {expandedMenu === 'applicants' && (
                                <div className="pl-4 pr-2 py-2 space-y-1 mt-1 border-l-2 border-yellow-200 ml-4">
                                    <button onClick={() => setActiveTab('applied')} className={`w-full text-left px-3 py-2 rounded-xl text-xs font-extrabold flex items-center justify-between transition-colors ${activeTab === 'applied' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-black hover:bg-gray-50'}`}>
                                        <span className="flex items-center gap-2"><FileText size={14}/> Applied</span>
                                        <span className="bg-gray-100 px-2 py-0.5 rounded-md text-[11px]">{appliedCandidates.length}</span>
                                    </button>
                                    <button onClick={() => setActiveTab('shortlisted')} className={`w-full text-left px-3 py-2 rounded-xl text-xs font-extrabold flex items-center justify-between transition-colors ${activeTab === 'shortlisted' ? 'text-emerald-600 bg-emerald-50' : 'text-gray-600 hover:text-black hover:bg-gray-50'}`}>
                                        <span className="flex items-center gap-2"><CheckCircle size={14}/> Shortlisted</span>
                                        <span className="bg-gray-100 px-2 py-0.5 rounded-md text-[11px]">{shortlistedCandidates.length}</span>
                                    </button>
                                    <button onClick={() => setActiveTab('rejected')} className={`w-full text-left px-3 py-2 rounded-xl text-xs font-extrabold flex items-center justify-between transition-colors ${activeTab === 'rejected' ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:text-black hover:bg-gray-50'}`}>
                                        <span className="flex items-center gap-2"><XCircle size={14}/> Rejected</span>
                                        <span className="bg-gray-100 px-2 py-0.5 rounded-md text-[11px]">{rejectedCandidates.length}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dynamic Content Sections */}
                    <div className="lg:col-span-3">
                        
                        {/* 1. MANAGE JOBS TAB */}
                        {activeTab === 'jobs' && (
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 md:p-8">
                                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                                    <h2 className="text-xl font-extrabold text-gray-900">Manage Active Jobs</h2>
                                    <button onClick={() => { setActiveTab('post-job'); setEditingJobId(null); setJobFormData(initialJobForm); setCurrentStep(1); }} className="text-sm bg-[#FED547] text-black px-4 py-2 rounded-xl font-bold hover:bg-yellow-400 shadow-sm transition-colors cursor-pointer">
                                        + Post Job
                                    </button>
                                </div>

                                {jobs.length === 0 ? (
                                    <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm"><Briefcase className="text-gray-300 w-8 h-8"/></div>
                                        <h3 className="text-gray-900 font-bold text-lg">No jobs posted yet</h3>
                                        <p className="text-gray-500 text-sm mt-1">Start building your team by posting a new job.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-5">
                                        {jobs.map(job => (
                                            <div key={job.id} className="p-5 rounded-2xl border border-gray-200 bg-white hover:border-yellow-400 hover:shadow-md transition-all space-y-4">
                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-3">
                                                            <h4 className="font-extrabold text-gray-900 text-lg">{job.title}</h4>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FED547]/20 text-yellow-800 px-2.5 py-1 rounded-md border border-yellow-200">{job.job_type}</span>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-4 text-[13px] font-semibold text-gray-500">
                                                            {job.location && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-gray-400"/> {job.location}</span>}
                                                            {job.salary_range && <span className="flex items-center gap-1.5"><DollarSign size={14} className="text-emerald-500"/> {job.salary_range}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button 
                                                            onClick={() => setSelectedJobApplicants(selectedJobApplicants === job.id ? null : job.id)}
                                                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
                                                        >
                                                            <Users size={14} /> 
                                                            {selectedJobApplicants === job.id ? 'Hide Candidates' : `Applicants (${job.candidates?.length || 0})`}
                                                        </button>
                                                        {/* 🌟 EDIT JOB BUTTON */}
                                                        <button onClick={() => handleEditJob(job)} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer" title="Edit Job">
                                                            <Edit size={16} />
                                                        </button>
                                                        <button onClick={() => handleDeleteJob(job.id)} className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors cursor-pointer" title="Delete Job">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Candidates List Accordion */}
                                                {selectedJobApplicants === job.id && (
                                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                                                        <h5 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-2">
                                                            <UserCheck size={14} className="text-[#FED547]" /> Candidates Applied
                                                        </h5>
                                                        {(!job.candidates || job.candidates.length === 0) ? (
                                                            <p className="text-sm text-gray-500 italic py-3 bg-gray-50 text-center rounded-xl">No candidates have applied yet.</p>
                                                        ) : (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                {job.candidates.map(candidate => (
                                                                    <div key={candidate.id} className="bg-gray-50 p-4 rounded-2xl border border-gray-200 text-sm space-y-2">
                                                                        <div className="font-extrabold text-gray-900">{candidate.name || candidate.full_name}</div>
                                                                        <div className="text-gray-600 font-medium flex items-center gap-2"><Mail size={14} className="text-blue-400"/> {candidate.email}</div>
                                                                        <div className="text-gray-600 font-medium flex items-center gap-2"><Phone size={14} className="text-emerald-400"/> {candidate.phone}</div>
                                                                        {candidate.resume_url && (
                                                                            <a href={STORAGE_URL(candidate.resume_url || candidate.resume)} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-3 py-1.5 bg-white border border-gray-200 text-blue-600 font-bold text-xs rounded-lg hover:border-blue-300 transition-colors shadow-sm">
                                                                                View Resume 📄
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* DYNAMIC APPLICANT VIEWS */}
                        {activeTab === 'applied' && renderApplicantsTable(appliedCandidates, 'New & Applied Candidates')}
                        {activeTab === 'shortlisted' && renderApplicantsTable(shortlistedCandidates, 'Shortlisted Candidates')}
                        {activeTab === 'rejected' && renderApplicantsTable(rejectedCandidates, 'Rejected Candidates')}

                        {/* 2. POST / EDIT NEW JOB TAB (WIZARD & PREVIEW) */}
                        {activeTab === 'post-job' && (
                            <div className="flex flex-col xl:flex-row gap-6">
                                
                                {/* WIZARD FORM */}
                                <div className="flex-1 bg-white rounded-3xl shadow-sm border border-gray-200 p-6 md:p-8">
                                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
                                        <h3 className="font-extrabold text-lg text-gray-900">{editingJobId ? 'Edit Job Posting' : 'Post New Job'}</h3>
                                    </div>
                                    <div className="flex items-center justify-between pb-8 mb-8 border-b border-gray-100">
                                        {STEPS.map((step, idx) => (
                                            <div key={step.id} className="flex items-center flex-1 last:flex-none">
                                                <div className={`flex flex-col items-center relative z-10 bg-white ${currentStep >= step.id ? 'text-black' : 'text-gray-400'}`}>
                                                    <span className="text-[10px] font-extrabold mb-1 uppercase tracking-wider hidden sm:block">Step {step.id}</span>
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${currentStep === step.id ? 'border-[#FED547] bg-[#FED547] text-black shadow-md' : (currentStep > step.id ? 'border-[#FED547] text-[#FED547]' : 'border-gray-200 text-gray-300')}`}>
                                                        <step.icon size={18} />
                                                    </div>
                                                </div>
                                                {idx < STEPS.length - 1 && (
                                                    <div className={`flex-1 h-1 mx-2 sm:mx-4 rounded-full transition-colors ${currentStep > step.id ? 'bg-[#FED547]' : 'bg-gray-100'}`}></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* STEP 1 */}
                                    {currentStep === 1 && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Basic Info</h2>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-2">Job Title *</label><input type="text" name="title" value={jobFormData.title} onChange={handleChange} placeholder="e.g. Frontend Developer" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white transition-all text-sm font-semibold" /></div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Category *</label>
                                                    <select name="category" value={jobFormData.category} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white transition-all text-sm font-semibold">
                                                        <option value="" disabled>Select Category</option>
                                                        {dbCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                                                    </select>
                                                </div>
                                                <div><label className="block text-sm font-bold text-gray-700 mb-2">Application Deadline *</label><input type="date" name="deadline" min={minDate} value={jobFormData.deadline} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white transition-all text-sm font-semibold text-gray-700" /></div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-3">Vacancy</label>
                                                <div className="flex flex-wrap gap-3">
                                                    {["1", "2", "5", "10"].map(v => <Pill key={v} label={v} active={jobFormData.vacancies === v} onClick={() => handlePillSelect('vacancies', v)} />)}
                                                    <input type="number" name="vacancies" value={jobFormData.vacancies} onChange={handleChange} placeholder="Custom" className="w-24 border border-gray-300 rounded-full px-4 py-1.5 text-sm font-bold outline-none text-center focus:border-[#FED547]" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Choose City *</label>
                                                    <select name="city" value={jobFormData.city} onChange={handleCityChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white transition-all text-sm font-semibold">
                                                        <option value="" disabled>Select City</option>
                                                        {dbCities.map((city, i) => <option key={i} value={city.name || city}>{city.name || city}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Choose Locality</label>
                                                    <select name="locality" value={jobFormData.locality} onChange={handleChange} disabled={!jobFormData.city || loadingLocalities} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white transition-all text-sm font-semibold disabled:bg-gray-100 disabled:opacity-70">
                                                        <option value="" disabled>{loadingLocalities ? "Loading..." : (!jobFormData.city ? "Select a city first" : "Select Locality")}</option>
                                                        {dbLocalities.map((loc, i) => <option key={i} value={loc.name || loc}>{loc.name || loc}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Job Type</label><div className="flex gap-2.5">{["Full-Time", "Part-Time", "Both"].map(t => <Pill key={t} label={t} active={jobFormData.job_type === t} onClick={() => handlePillSelect('job_type', t)} />)}</div></div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Location Type</label><div className="flex gap-2.5">{["Work from office", "Field job", "Work from home"].map(l => <Pill key={l} label={l} active={jobFormData.work_location_type === l} onClick={() => handlePillSelect('work_location_type', l)} />)}</div></div>
                                        </div>
                                    )}

                                    {/* STEP 2 */}
                                    {currentStep === 2 && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Salary & Perks</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div><label className="block text-sm font-bold text-gray-700 mb-2">Min Salary (₹) *</label><input type="number" name="salary_min" value={jobFormData.salary_min} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white text-sm font-semibold" placeholder="e.g. 15000" /></div>
                                                <div><label className="block text-sm font-bold text-gray-700 mb-2">Max Salary (₹) *</label><input type="number" name="salary_max" value={jobFormData.salary_max} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white text-sm font-semibold" placeholder="e.g. 25000" /></div>
                                            </div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Shifts</label><div className="flex flex-wrap gap-2.5">{["Day", "Night", "Rotational", "Flexible"].map(s => <Pill key={s} label={s} active={jobFormData.shift === s} onClick={() => handlePillSelect('shift', s)} />)}</div></div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Benefits Offered</label><div className="flex flex-wrap gap-2.5">{["Cab", "Meal", "Insurance", "PF", "Medical Benefits"].map(b => <Pill key={b} label={jobFormData.benefits.includes(b)?`✓ ${b}`:`+ ${b}`} active={jobFormData.benefits.includes(b)} onClick={() => handleMultiSelect('benefits', b)} />)}</div></div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Interview Type</label><div className="flex gap-2.5">{["In-person", "Telephonic"].map(i => <Pill key={i} label={i} active={jobFormData.interview_type === i} onClick={() => handlePillSelect('interview_type', i)} />)}</div></div>
                                        </div>
                                    )}

                                    {/* STEP 3 */}
                                    {currentStep === 3 && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Candidate Requirements</h2>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Gender</label><div className="flex gap-2.5">{["Any", "Male", "Female"].map(g => <Pill key={g} label={g} active={jobFormData.gender === g} onClick={() => handlePillSelect('gender', g)} />)}</div></div>
                                            <div><label className="block text-sm font-bold text-gray-700 mb-3">Qualification</label><div className="flex flex-wrap gap-2.5">{["Any", "10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate"].map(q => <Pill key={q} label={q} active={jobFormData.qualification === q} onClick={() => handlePillSelect('qualification', q)} />)}</div></div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-3">Experience Needed</label>
                                                <div className="flex gap-4">
                                                    <select name="exp_min" value={jobFormData.exp_min} onChange={handleChange} disabled={jobFormData.freshers_only} className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none disabled:bg-gray-100 disabled:opacity-70 text-sm font-semibold"><option value="0">0 Years</option><option value="1">1 Year</option><option value="2">2 Years</option><option value="3">3+ Years</option></select>
                                                    <select name="exp_max" value={jobFormData.exp_max} onChange={handleChange} disabled={jobFormData.freshers_only} className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none disabled:bg-gray-100 disabled:opacity-70 text-sm font-semibold"><option value="1">1 Year</option><option value="3">3 Years</option><option value="5">5 Years</option><option value="10">10+ Years</option></select>
                                                </div>
                                                <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm font-bold text-yellow-800 bg-yellow-50 w-max px-3 py-1.5 rounded-lg border border-yellow-200"><input type="checkbox" name="freshers_only" checked={jobFormData.freshers_only} onChange={handleChange} className="w-4 h-4 rounded text-[#FED547]" /> Only freshers should apply</label>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-3">Required Skills * (Type & Press Enter)</label>
                                                <div className="flex flex-wrap gap-2 mb-3">{jobFormData.skills.map((skill, index) => <span key={index} className="flex items-center gap-1.5 bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">{skill}<button type="button" onClick={() => removeSkill(skill)} className="text-gray-300 hover:text-red-400"><X size={14}/></button></span>)}</div>
                                                <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={handleSkillKeyDown} placeholder="e.g. Communication, Tally" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white text-sm font-semibold"/>
                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 4 */}
                                    {currentStep === 4 && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Job Description</h2>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">Write detailed responsibilities and requirements *</label>
                                                <textarea name="description" value={jobFormData.description} onChange={handleChange} rows={10} placeholder="Type here..." className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FED547] focus:bg-white text-sm font-semibold resize-none"></textarea>
                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 5 */}
                                    {currentStep === 5 && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Review & Confirm</h2>
                                            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="font-extrabold text-lg text-gray-800">Job Summary</h3>
                                                    <button onClick={() => setCurrentStep(1)} className="text-blue-600 text-sm font-bold hover:underline">Edit</button>
                                                </div>
                                                <div className="grid grid-cols-2 gap-y-4 text-[13px]">
                                                    <div><span className="text-gray-500 font-semibold block mb-1">Title</span><span className="font-bold text-gray-900">{jobFormData.title}</span></div>
                                                    <div><span className="text-gray-500 font-semibold block mb-1">Category</span><span className="font-bold text-gray-900">{jobFormData.category}</span></div>
                                                    <div><span className="text-gray-500 font-semibold block mb-1">Location</span><span className="font-bold text-gray-900">{jobFormData.locality ? `${jobFormData.locality}, ${jobFormData.city}` : jobFormData.city}</span></div>
                                                    <div><span className="text-gray-500 font-semibold block mb-1">Salary</span><span className="font-bold text-gray-900">₹{jobFormData.salary_min} - ₹{jobFormData.salary_max}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Bar */}
                                    <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between">
                                        <button type="button" onClick={handlePrev} disabled={currentStep === 1} className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer">
                                            <ChevronLeft size={18} /> Back
                                        </button>
                                        
                                        {currentStep < 5 ? (
                                            <button type="button" onClick={handleNext} className="flex items-center gap-2 bg-[#FED547] hover:bg-yellow-400 text-black font-extrabold py-3 px-8 rounded-xl transition-colors shadow-sm cursor-pointer">
                                                Next Step <ChevronRight size={18} />
                                            </button>
                                        ) : (
                                            <button type="button" onClick={handleSaveJob} disabled={loading} className="flex items-center gap-2 bg-black hover:bg-gray-800 text-[#FED547] font-extrabold py-3 px-8 rounded-xl transition-colors shadow-lg shadow-black/20 disabled:opacity-50 cursor-pointer">
                                                {loading ? "Saving..." : (editingJobId ? "Update Job" : "Publish Job")} <Save size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Live Preview Widget */}
                                <div className="hidden xl:block w-80 shrink-0">
                                    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm sticky top-32 overflow-hidden">
                                        <div className="bg-gray-50 border-b border-gray-100 p-4">
                                            <h3 className="text-xs font-extrabold text-gray-500 uppercase tracking-wider">Live Job Preview</h3>
                                        </div>
                                        <div className="p-5">
                                            <h1 className="text-lg font-black text-gray-900 mb-1 leading-tight">{jobFormData.title || "Job Title Here"}</h1>
                                            <div className="text-[13px] font-bold text-gray-500 mb-4 flex items-center gap-1.5">
                                                <Briefcase size={14}/> {employer.company_name || 'Your Company'}
                                            </div>
                                            
                                            {(jobFormData.salary_min || jobFormData.salary_max) && (
                                                <p className="text-black font-extrabold mb-3 flex items-center gap-1.5 bg-yellow-50 px-3 py-1.5 rounded-lg w-max border border-yellow-200">
                                                    ₹{jobFormData.salary_min || "Min"} - ₹{jobFormData.salary_max || "Max"} 
                                                </p>
                                            )}
                                            
                                            <div className="space-y-2.5 text-[13px] font-semibold text-gray-600 mb-5">
                                                <div className="flex items-start gap-2"><MapPin size={16} className="shrink-0 text-gray-400" /><span>{jobFormData.city ? (jobFormData.locality ? `${jobFormData.locality}, ${jobFormData.city}` : jobFormData.city) : "Location not set"}</span></div>
                                                <div className="flex items-start gap-2"><User size={16} className="shrink-0 text-gray-400" /><span>{jobFormData.freshers_only ? "Fresher" : `${jobFormData.exp_min}-${jobFormData.exp_max} Years`}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </main>

           {/* Profile Edit Modal */}
{isEditingProfile && createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50">
                <h3 className="font-extrabold text-xl text-gray-900">Edit Company Profile</h3>
                <button onClick={() => setIsEditingProfile(false)} className="text-gray-400 hover:text-black bg-white p-2 rounded-full border shadow-sm"><X size={18}/></button>
            </div>
            <form onSubmit={handleUpdateProfile} className="p-6 space-y-4 text-sm font-semibold">
                <div><label className="block text-gray-600 mb-1.5">Company Name</label><input type="text" value={profileData.company_name || ''} onChange={e => setProfileData({...profileData, company_name: e.target.value})} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-400" required /></div>
                
                <div><label className="block text-gray-600 mb-1.5">Contact Person Name</label><input type="text" value={profileData.name || ''} onChange={e => setProfileData({...profileData, name: e.target.value})} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-400" required /></div>
                
                <div><label className="block text-gray-600 mb-1.5">Email</label><input type="email" value={profileData.email || ''} onChange={e => setProfileData({...profileData, email: e.target.value})} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-400" required /></div>
                
                {/* 🌟 PHONE NUMBER DISABLED KIYA GAYA HAI */}
                <div>
                    <label className="block text-gray-600 mb-1.5">Phone Number (Registered)</label>
                    <input 
                        type="text" 
                        value={profileData.phone || ''} 
                        disabled 
                        className="w-full p-3 bg-gray-100 border border-gray-200 rounded-xl outline-none text-gray-400 cursor-not-allowed" 
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Primary number cannot be changed directly.</p>
                </div>
                
                <div><label className="block text-gray-600 mb-1.5">Website URL</label><input type="url" value={profileData.website || ''} onChange={e => setProfileData({...profileData, website: e.target.value})} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-400" /></div>
                
                <button type="submit" className="w-full bg-[#FED547] text-black font-extrabold py-3.5 rounded-xl hover:bg-yellow-400 transition-colors mt-6 shadow-md">Save Changes</button>
            </form>
        </div>
    </div>,
    document.body
)}

            {/* Logout Modal */}
            {showLogoutConfirm && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-[2rem] w-full max-w-sm overflow-hidden shadow-2xl p-8 text-center animate-in zoom-in-95 duration-200 border border-gray-100">
                        <div className="w-20 h-20 bg-red-50 border-8 border-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6"><LogOut size= {32} /></div>
                        <h3 className="font-black text-2xl text-gray-900 mb-2">Leaving so soon?</h3>
                        <p className="text-gray-500 text-sm font-semibold mb-8">Are you sure you want to log out from your employer dashboard?</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50">Cancel</button>
                            <button onClick={confirmLogout} className="flex-1 py-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-600/30">Yes, Logout</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
}